import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CalendarRange, Loader2, RefreshCw, Send, Users, Phone, GraduationCap, MessageSquareText, Dumbbell, MessageCircle, PhoneCall, UserCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { getGymRoster, buildGymRoster, buildEveryGymRoster, publishGymRoster, editRosterDraftSession, setRosterPeople, copyRosterSessions } from "@/lib/api";

// The gym roster (backend routes/gym_roster.py): Large Group sessions with 2 coaches each, sales consult
// hours from the gym's GoHighLevel consult calendar (the sales team stays off sessions then), the
// facility head's 4 sessions, accountability replies, client messages, Thursday calls, member check-ins and
// training, shown as one box per day and hour. Built as a draft; the facility
// head publishes it and clock-in follows it from then.

const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const WEEK = "ABCDEFG";
const KIND = {
  consults: { label: "Sales consults", icon: Phone, cls: "border-amber-500/40 bg-amber-500/10 text-amber-100" },
  sales_training: { label: "Sales training (Zoom)", icon: GraduationCap, cls: "border-sky-500/40 bg-sky-500/10 text-sky-100" },
  role_play: { label: "Sales role play", icon: GraduationCap, cls: "border-sky-500/40 bg-sky-500/10 text-sky-100" },
  coach_course: { label: "Coach course", icon: GraduationCap, cls: "border-violet-500/40 bg-violet-500/10 text-violet-100" },
  replies: { label: "Accountability replies", icon: MessageSquareText, cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100" },
  client_messages: { label: "Client messages", icon: MessageCircle, cls: "border-teal-500/40 bg-teal-500/10 text-teal-100" },
  thursday_calls: { label: "Thursday calls", icon: PhoneCall, cls: "border-rose-500/40 bg-rose-500/10 text-rose-100" },
  mm_checkins: { label: "Member check-ins", icon: UserCheck, cls: "border-orange-500/40 bg-orange-500/10 text-orange-100" },
};
const first = (name) => (name || "?").split(" ")[0];
const inBy = (hhmm) => {                          // clock in 10 minutes before the shift starts
  const [h, m] = hhmm.split(":").map(Number);
  const t = h * 60 + m - 10;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Pill({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 h-8 rounded-full text-xs font-medium ${active ? "bg-zinc-100 text-zinc-900" : "text-zinc-400 hover:text-zinc-100"}`}>
      {children}
    </button>
  );
}

function SessionCard({ s, coaches, names, fh, editable, team, onSave }) {
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(coaches);
  useEffect(() => setPick(coaches), [coaches]);
  const short = coaches.length < 2;
  return (
    <div className={`rounded-lg border px-2.5 py-2 text-xs ${short ? "border-rose-500/60 bg-rose-500/10" : "border-[#262626] bg-[#0d0d0d]"}`}
      data-testid={`roster-session-${s.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-zinc-300 inline-flex items-center gap-1"><Dumbbell className="h-3 w-3 text-zinc-500" />{s.start_time}</span>
        {editable && !editing && <button type="button" className="text-[10px] text-zinc-500 hover:text-zinc-200" onClick={() => setEditing(true)}>Change</button>}
      </div>
      {!editing ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {coaches.length === 0 && <span className="text-rose-300">No coach</span>}
          {coaches.map((u) => (
            <span key={u} className={`px-1.5 py-0.5 rounded ${u === fh ? "bg-violet-500/20 text-violet-200" : "bg-zinc-800 text-zinc-200"}`}
              title={u === fh ? "Facility head" : undefined}>{first(names[u])}</span>
          ))}
          {short && coaches.length > 0 && <span className="text-rose-300">needs 2</span>}
        </div>
      ) : (
        <div className="mt-1.5 space-y-1">
          {[0, 1].map((i) => (
            <select key={i} value={pick[i] || ""} onChange={(e) => { const p = [...pick]; p[i] = e.target.value; setPick(p.filter((x, j) => x || j < i)); }}
              className="w-full bg-[#0a0a0a] border border-[#262626] rounded px-1.5 py-1 text-xs text-zinc-100">
              <option value="">No one</option>
              {team.map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}
            </select>
          ))}
          <div className="flex gap-1">
            <button type="button" className="flex-1 h-7 rounded bg-zinc-100 text-zinc-900 font-semibold"
              onClick={async () => { await onSave(s.id, pick.filter(Boolean)); setEditing(false); }}>Save</button>
            <button type="button" className="h-7 px-2 rounded border border-[#2a2a2a] text-zinc-400" onClick={() => { setPick(coaches); setEditing(false); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const hourOf = (hhmm) => parseInt(String(hhmm).slice(0, 2), 10);
const minsOf = (hhmm) => { const [h, m] = String(hhmm).split(":").map(Number); return h * 60 + m; };
const hourLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;
// What goes in each box, in this order, after the session itself
const BOX_ORDER = ["replies", "client_messages", "thursday_calls", "mm_checkins", "coach_course", "role_play", "sales_training", "consults"];

// One box per day and hour: the session that starts then (with its coaches) and everyone doing something else
// in that hour (accountability replies, client messages, Thursday calls, check-ins, training, consults).
function HourBox({ sessions, blocks, wk, names, fh, editable, team, onSave }) {
  const byKind = {};
  blocks.forEach((b) => { (byKind[b.kind] = byKind[b.kind] || []).push(b); });
  const empty = !sessions.length && !blocks.length;
  return (
    <td className="align-top p-1 min-w-[170px]">
      <div className={`h-full rounded-lg border p-2 space-y-1.5 text-xs ${empty ? "border-transparent" : "border-[#262626] bg-[#0d0d0d]"}`}>
        {sessions.map((s) => (
          <SessionCard key={s.id} s={s} coaches={wk.sessions[s.id] || []} names={names} fh={fh} editable={editable} team={team} onSave={onSave} />
        ))}
        {BOX_ORDER.filter((k) => byKind[k]).map((k) => {
          const K = KIND[k];
          const rows = byKind[k].sort((a, b) => a.start.localeCompare(b.start));
          return (
            <div key={k} className={`rounded-md border px-2 py-1.5 ${K.cls}`}>
              <div className="flex items-center gap-1 font-medium"><K.icon className="h-3 w-3" />{K.label}</div>
              <div className="mt-0.5 space-y-0.5">
                {rows.map((b, i) => {
                  const note = b.label.includes(" · ") ? b.label.split(" · ").slice(1).join(" · ") : "";
                  return (
                    <div key={i} className="flex justify-between gap-2">
                      <span>{first(names[b.user_id])}{note ? <span className="opacity-60"> · {note}</span> : null}</span>
                      <span className="font-mono opacity-70 whitespace-nowrap">{b.start}–{b.end}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </td>
  );
}

function Timetable({ plan, week, editable, team, onSave }) {
  const wk = plan.weeks[week];
  const names = plan.names || {};
  const blocks = wk.blocks.filter((b) => b.kind !== "shift");
  const days = [0, 1, 2, 3, 4, 5, 6].filter((d) => plan.session_list.some((s) => s.day_of_week === d) || blocks.some((b) => b.day === d));
  // every hour anything happens in, morning to night
  const hours = new Set(plan.session_list.map((s) => hourOf(s.start_time)));
  blocks.forEach((b) => { for (let h = hourOf(b.start); h * 60 < minsOf(b.end); h++) hours.add(h); });
  const rows = [...hours].sort((a, b) => a - b);
  const inHour = (b, h) => minsOf(b.start) < (h + 1) * 60 && minsOf(b.end) > h * 60;
  return (
    <div className="card-surface overflow-x-auto" data-testid="roster-timetable">
      <table className="border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-[#0a0a0a] w-16" />
            {days.map((d) => <th key={d} className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-medium px-2 py-2">{DAY_NAMES[d]}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h} className="border-t border-[#1a1a1a]">
              <th className="sticky left-0 z-10 bg-[#0a0a0a] text-right pr-2 pt-2 align-top text-xs font-mono text-zinc-500 whitespace-nowrap">{hourLabel(h)}</th>
              {days.map((d) => (
                <HourBox key={d} wk={wk} names={names} fh={plan.facility_head} editable={editable} team={team} onSave={onSave}
                  sessions={plan.session_list.filter((s) => s.day_of_week === d && hourOf(s.start_time) === h)}
                  blocks={blocks.filter((b) => b.day === d && inHour(b, h))} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-zinc-500 px-3 py-2 border-t border-[#1a1a1a]">Each box is one hour: the session and its coaches, then who is doing what else in that hour.</p>
    </div>
  );
}

function PeopleTable({ plan, week }) {
  const DAYS = DAY_NAMES;
  const used = [0, 1, 2, 3, 4, 5, 6].filter((d) => plan.people.some((p) => p.weeks[week]?.shifts?.[d]));
  return (
    <div className="card-surface overflow-x-auto" data-testid="roster-people">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
            <th className="px-4 py-3 font-medium">Person</th>
            <th className="px-4 py-3 font-medium text-right">Sessions</th>
            {used.map((d) => <th key={d} className="px-3 py-3 font-medium">{DAYS[d]}</th>)}
          </tr>
        </thead>
        <tbody>
          {plan.people.map((p) => {
            const w = p.weeks[week] || {};
            return (
              <tr key={p.user_id} className="border-b border-[#1a1a1a] last:border-0 align-top">
                <td className="px-4 py-3">
                  <div className="text-zinc-100">{p.name}</div>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {p.user_id === plan.facility_head && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-200">Facility head</span>}
                    {p.on_consults && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200">Consults</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-200">{w.sessions || 0}</td>
                {used.map((d) => {
                  const s = w.shifts?.[d];
                  return (
                    <td key={d} className="px-3 py-3 text-xs font-mono whitespace-nowrap text-zinc-300">
                      {["morning", "evening"].filter((k) => s?.[k]).map((k) => (
                        <div key={k}>{s[k][0]}–{s[k][1]} <span className="text-zinc-600">in {inBy(s[k][0])}</span></div>
                      ))}
                      {!s && <span className="text-zinc-700">off</span>}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-zinc-500 px-4 py-3 border-t border-[#1a1a1a]">A shift starts at the person's first session and runs to 11 AM or 9 PM. "in" is when to clock in: 10 minutes before the start. Training and replies are on the timetable.</p>
    </div>
  );
}

export default function Roster() {
  const { user, activeFacility, facilities, effectiveRole } = useAuth();
  const role = effectiveRole || user?.role;
  const manager = atLeast(role, "manager");
  const admin = atLeast(role, "admin");
  const [data, setData] = useState(null);
  const [which, setWhich] = useState("published");
  const [week, setWeek] = useState(0);
  const [view, setView] = useState("timetable");
  const [busy, setBusy] = useState("");
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await getGymRoster();
      setData(d);
      setWeek(d.this_week || 0);
      setWhich((cur) => (d.draft && (!d.published || cur === "draft") ? "draft" : "published"));
    } catch (e) { toast.error(err(e, "Couldn't load the roster")); }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const plan = which === "draft" ? data?.draft : data?.published;
  const team = useMemo(() => data?.team || [], [data]);
  const act = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); await load(); }
    catch (e) { toast.error(err(e, "That didn't work")); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="roster-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Roster · {activeFacility?.name || "this gym"}</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Roster</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            2 coaches on every Large Group session. Whoever is on the GoHighLevel consult calendar is on consults for its open hours and
            stays off sessions then. The facility head takes 4 sessions a week at different times. Early and late sessions rotate
            between Week A and Week B. Each morning and evening has two starts (like 6 and 7 AM, 5 and 6 PM) with a new pair each; who opens rotates.
            Shifts run to 11 AM or 9 PM, and everyone clocks in 10 minutes before their first session.
          </p>
        </div>
        {manager && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" disabled={!!busy} onClick={() => act("build", buildGymRoster, "Draft built from GoHighLevel")} data-testid="roster-build">
              {busy === "build" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}<span className="ml-1.5">Build draft</span>
            </Button>
            {admin && (
              <Button variant="outline" size="sm" disabled={!!busy} onClick={() => act("all", buildEveryGymRoster, "Drafts built for every gym")} data-testid="roster-build-all">
                {busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarRange className="h-4 w-4" />}<span className="ml-1.5">Build every gym</span>
              </Button>
            )}
            {data?.draft && !confirmPublish && (
              <Button size="sm" disabled={!!busy} onClick={() => setConfirmPublish(true)} data-testid="roster-publish">
                <Send className="h-4 w-4" /><span className="ml-1.5">Publish draft</span>
              </Button>
            )}
            {confirmPublish && (
              <div className="flex items-center gap-2 text-xs text-zinc-300">
                Clock-in will expect people as rostered, from this week.
                <Button size="sm" disabled={!!busy} onClick={() => act("publish", publishGymRoster, "Roster published").then(() => { setConfirmPublish(false); setWhich("published"); })}
                  data-testid="roster-publish-confirm">Publish</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmPublish(false)}>Cancel</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-3">
          {manager && (
            <div className="flex items-center gap-1 card-surface p-1 rounded-full">
              <Pill active={which === "published"} onClick={() => setWhich("published")} testid="roster-show-published">Published</Pill>
              <Pill active={which === "draft"} onClick={() => setWhich("draft")} testid="roster-show-draft">Draft</Pill>
            </div>
          )}
          {plan && (
            <div className="flex items-center gap-1 card-surface p-1 rounded-full">
              {plan.weeks.map((_, i) => (
                <Pill key={i} active={week === i} onClick={() => setWeek(i)} testid={`roster-week-${i}`}>
                  Week {WEEK[i]}{which === "published" && data.anchor && i === data.this_week ? " · this week" : ""}
                </Pill>
              ))}
            </div>
          )}
          {plan && (
            <div className="flex items-center gap-1 card-surface p-1 rounded-full">
              <Pill active={view === "timetable"} onClick={() => setView("timetable")}>Timetable</Pill>
              <Pill active={view === "people"} onClick={() => setView("people")}><Users className="h-3 w-3 inline mr-1" />People</Pill>
            </div>
          )}
          <span className="text-xs text-zinc-500">
            {which === "published" && data.published_at && `Published ${new Date(data.published_at).toLocaleString()} by ${data.published_by}`}
            {which === "draft" && plan && `Draft built ${new Date(plan.built_at).toLocaleString()} by ${plan.built_by}. Not live until published.`}
          </span>
        </div>
      )}

      {!plan && data && (
        <div className="card-surface p-6 text-sm text-zinc-400">
          {which === "published" ? "No roster published for this gym yet." : "No draft yet."}
          {manager && " Press Build draft to make one from the session times and the GoHighLevel consult calendar."}
        </div>
      )}

      {plan && plan.warnings?.length > 0 && (
        <section className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 space-y-1.5" data-testid="roster-warnings">
          <div className="text-xs uppercase tracking-[0.18em] text-amber-300 inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Needs a look</div>
          {plan.warnings.map((w, i) => <p key={i} className="text-sm text-amber-100/90">{w}</p>)}
        </section>
      )}

      {plan && plan.consults && (
        <section className="card-surface p-4 text-sm space-y-2" data-testid="roster-consults">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Consult calendar in GoHighLevel</div>
          {(plan.consults.calendars || []).length === 0 && <p className="text-zinc-400">{plan.consults.error || "None found."}</p>}
          {(plan.consults.calendars || []).map((c) => (
            <div key={c.name}>
              <div className="text-zinc-100">{c.name} <span className="text-zinc-500 text-xs">({c.source === "bookings" ? "no open hours set: from bookings" : "open hours"})</span></div>
              <div className="text-xs text-zinc-400 font-mono mt-0.5">
                {Object.entries(c.windows || {}).map(([d, ivs]) => `${DAY_NAMES[d]} ${ivs.map((x) => x.join("–")).join(", ")}`).join(" · ")}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">On it: {(c.team || []).map((t) => (plan.names || {})[t.user_id] || t.ghl_name).join(", ") || "no one"}</div>
            </div>
          ))}
        </section>
      )}

      {plan && plan.session_list.length === 0 && manager && which === "draft" && (
        <section className="card-surface p-4 text-sm flex flex-wrap items-center gap-2">
          <span className="text-zinc-300">No sessions at this gym. Copy another gym's session times:</span>
          <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}
            className="bg-[#0a0a0a] border border-[#262626] rounded-md px-2 py-1.5 text-sm text-zinc-100" data-testid="roster-copy-from">
            <option value="">Pick a gym</option>
            {(facilities || []).filter((f) => f.id !== activeFacility?.id).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <Button size="sm" variant="outline" disabled={!copyFrom || !!busy}
            onClick={() => act("copy", async () => { const r = await copyRosterSessions(copyFrom); await buildGymRoster(); return r; }, (r) => `${r.copied} sessions copied`)}>
            Copy and rebuild
          </Button>
          <span className="text-xs text-zinc-500">They also show on the check-in tablet. Change them any time in Settings → Session schedules.</span>
        </section>
      )}

      {plan && view === "timetable" && plan.weeks[week] && (
        <Timetable plan={plan} week={week} editable={manager && which === "draft"} team={team}
          onSave={(sid, uids) => act("edit", () => editRosterDraftSession({ session_id: sid, week, user_ids: uids }), "Saved in the draft")} />
      )}
      {plan && view === "people" && plan.weeks[week] && <PeopleTable plan={plan} week={week} />}

      {manager && which === "draft" && data?.draft && (
        <section className="card-surface p-4 space-y-3" data-testid="roster-setup">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Who's on this gym's roster</div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            Facility head
            <select value={data.facility_head_user_id || data.draft.facility_head || ""} data-testid="roster-fh"
              onChange={(e) => act("fh", () => setRosterPeople({ facility_head_user_id: e.target.value }).then(buildGymRoster), "Facility head saved, draft rebuilt")}
              className="bg-[#0a0a0a] border border-[#262626] rounded-md px-2 py-1.5 text-sm text-zinc-100">
              <option value="">Pick</option>
              {team.map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            On consults at a time
            <select value={data.consult_cover || 0} data-testid="roster-consult-cover"
              onChange={(e) => act("cover", () => setRosterPeople({ consult_cover: Number(e.target.value) }).then(buildGymRoster), "Saved, draft rebuilt")}
              className="bg-[#0a0a0a] border border-[#262626] rounded-md px-2 py-1.5 text-sm text-zinc-100">
              <option value={0}>Everyone on the consult calendar</option>
              {[1, 2, 3].map((n) => <option key={n} value={n}>{n}, taking turns (the rest coach)</option>)}
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            {[...data.draft.people.map((p) => ({ ...p, on: true })), ...(data.draft.away || []).map((p) => ({ ...p, on: false }))].map((p) => (
              <button key={p.user_id} type="button" disabled={!!busy}
                onClick={() => act("people", () => setRosterPeople({ people: { [p.user_id]: p.on ? "out" : "in" } }).then(buildGymRoster),
                  `${p.name} ${p.on ? "taken off" : "added"}, draft rebuilt`)}
                className={`text-xs px-2.5 py-1 rounded-full border ${p.on ? "border-emerald-500/40 text-emerald-200" : "border-[#2a2a2a] text-zinc-500 line-through"}`}
                title={p.on ? "On this gym's roster. Click to take them off." : "Not on this gym's roster. Click to add."}>
                {p.name}
              </button>
            ))}
          </div>
          <p className="text-xs text-zinc-500">Coaches come from the Large Group coach skill in User Management; the sales team from the GoHighLevel consult calendar.</p>
        </section>
      )}
    </div>
  );
}
